import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from './ui/dialog';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Separator } from './ui/separator';
import { Checkbox } from './ui/checkbox';
import {
  Link2,
  CheckCircle,
  Loader2,
  Factory,
  AlertTriangle
} from 'lucide-react';
import productionService from '../services/productionService';

/**
 * LinkProductionDialog - 2-step wizard for linking existing unassigned production orders to a Sales Order
 *
 * Step 1: Select unassigned production orders (multi-select with client filtering)
 * Step 2: Review product matching and confirm linking
 */
const LinkProductionDialog = ({ open, onClose, salesOrderId, salesOrderData, onLinked }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const [unassignedPRDs, setUnassignedPRDs] = useState([]);
  const [selectedPRDs, setSelectedPRDs] = useState([]);
  const [prdMappings, setPrdMappings] = useState([]);

  const steps = ['选择工单', '确认关联'];

  // Build SO product lookup table: product_id -> { line_item_id, name, quantity }
  const buildSOProductMap = useCallback(() => {
    const map = {};
    const soProducts = salesOrderData?.products || [];
    soProducts.forEach(p => {
      const productCode = p.product_code || p.product_id || p.part_number;
      const lineItemId = p.line_item_id || p._id;
      if (productCode) {
        map[productCode] = {
          line_item_id: lineItemId,
          name: p.name || '',
          quantity: parseFloat(p.quantity || 0)
        };
      }
    });
    return map;
  }, [salesOrderData]);

  // Fetch unassigned production orders
  const fetchUnassignedPRDs = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const clientId = salesOrderData?.client_ref;
      const filters = clientId ? { client_id: clientId } : {};

      const response = await productionService.getUnassignedProductionOrders(1, 100, filters);
      const prdList = response?.production_orders || [];
      setUnassignedPRDs(prdList);
    } catch (err) {
      console.error('Error fetching unassigned PRDs:', err);
      setError('获取未关联工单失败: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  }, [salesOrderData]);

  useEffect(() => {
    if (open) {
      fetchUnassignedPRDs();
    }
  }, [open, fetchUnassignedPRDs]);

  // Step 1: Handle PRD selection
  const handlePRDSelection = (prd, isSelected) => {
    if (isSelected) {
      setSelectedPRDs([...selectedPRDs, prd]);
    } else {
      setSelectedPRDs(selectedPRDs.filter(p => p._id !== prd._id));
    }
  };

  const isAllSelected = unassignedPRDs.length > 0 && selectedPRDs.length === unassignedPRDs.length;

  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelectedPRDs([]);
    } else {
      setSelectedPRDs([...unassignedPRDs]);
    }
  };

  // Step 2: Initialize PRD mappings
  const initializeMappings = () => {
    const soProductMap = buildSOProductMap();

    const mappings = selectedPRDs.map(prd => {
      const items = prd.items || [];
      const isMultiItem = items.length > 0;

      let itemMappings = [];

      if (isMultiItem) {
        // Multi-item: match each item's finished_product_code
        itemMappings = items.map((item, idx) => {
          const productCode = item.finished_product_code || item.finished_product_id;
          const soProduct = soProductMap[productCode];
          const plannedQty = parseFloat(item.planned_quantity || 0);

          return {
            prd_item_index: idx,
            product_code: productCode,
            so_product_id: productCode,
            so_line_item_id: soProduct?.line_item_id || null,
            so_product_name: soProduct?.name || '',
            so_required_qty: soProduct?.quantity || 0,
            prd_planned_qty: plannedQty,
            allocated_qty: soProduct ? Math.min(plannedQty, soProduct.quantity) : plannedQty,
            match: !!soProduct,
          };
        });
      } else {
        // Legacy single-item
        const productCode = prd.finished_product_code || prd.finished_product_id;
        const soProduct = soProductMap[productCode];
        const plannedQty = parseFloat(prd.planned_quantity || 0);

        itemMappings = [{
          prd_item_index: 0,
          product_code: productCode,
          so_product_id: productCode,
          so_line_item_id: soProduct?.line_item_id || null,
          so_product_name: soProduct?.name || '',
          so_required_qty: soProduct?.quantity || 0,
          prd_planned_qty: plannedQty,
          allocated_qty: soProduct ? Math.min(plannedQty, soProduct.quantity) : plannedQty,
          match: !!soProduct,
        }];
      }

      return {
        prd_id: prd._id,
        prd_number: prd.production_order_id || prd._id,
        work_center_name: prd.work_center_name || '',
        item_mappings: itemMappings,
      };
    });

    setPrdMappings(mappings);
  };

  // Handle quantity change
  const handleQuantityChange = (prdIndex, itemIndex, newQty) => {
    const updated = [...prdMappings];
    const mapping = updated[prdIndex].item_mappings[itemIndex];

    const qty = parseFloat(newQty);
    if (isNaN(qty) || qty < 0) return;

    const maxQty = Math.min(mapping.so_required_qty || Infinity, mapping.prd_planned_qty);
    mapping.allocated_qty = Math.min(qty, maxQty);

    setPrdMappings(updated);
  };

  // Navigation
  const handleNext = () => {
    if (activeStep === 0) {
      if (selectedPRDs.length === 0) {
        setError('请至少选择一个工单');
        return;
      }
      initializeMappings();
    }

    setError('');
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setError('');
    setActiveStep((prev) => prev - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
    setSelectedPRDs([]);
    setPrdMappings([]);
    setError('');
  };

  // Submit
  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      setError('');

      const prdAllocations = prdMappings
        .map(prd => ({
          prd_id: prd.prd_id,
          item_mappings: prd.item_mappings
            .filter(im => im.allocated_qty > 0 && im.so_line_item_id)
            .map(im => ({
              prd_item_index: im.prd_item_index,
              so_product_id: im.so_product_id,
              so_line_item_id: im.so_line_item_id,
              allocated_qty: im.allocated_qty,
            })),
        }))
        .filter(prd => prd.item_mappings.length > 0);

      if (prdAllocations.length === 0) {
        setError('没有可关联的工单映射（请确保产品有匹配）');
        setSubmitting(false);
        return;
      }

      const result = await productionService.linkProductionOrdersToSalesOrder(
        salesOrderId,
        prdAllocations
      );

      if (onLinked) {
        onLinked(result);
      }

      handleClose();
    } catch (err) {
      console.error('Error linking PRDs to SO:', err);
      setError('关联工单失败: ' + (err.response?.data?.error || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  // Stepper
  const Stepper = () => (
    <div className="flex items-center justify-center mb-6">
      {steps.map((label, index) => (
        <React.Fragment key={label}>
          <div className="flex items-center">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                index < activeStep
                  ? 'bg-success/100 text-white'
                  : index === activeStep
                  ? 'bg-primary/100 text-white'
                  : 'bg-muted/80 text-muted-foreground'
              }`}
            >
              {index < activeStep ? <CheckCircle className="h-5 w-5" /> : index + 1}
            </div>
            <span
              className={`ml-2 text-sm ${
                index === activeStep ? 'font-medium text-primary' : 'text-muted-foreground'
              }`}
            >
              {label}
            </span>
          </div>
          {index < steps.length - 1 && (
            <div
              className={`w-16 h-0.5 mx-2 ${
                index < activeStep ? 'bg-success/100' : 'bg-muted/80'
              }`}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  // Step 1: Select PRDs
  const renderStep1SelectPRDs = () => (
    <div>
      <p className="text-sm text-muted-foreground mb-4">
        选择要关联到当前销售订单的未分配工单
      </p>

      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : unassignedPRDs.length === 0 ? (
        <Alert>
          <AlertDescription>暂无未关联的工单</AlertDescription>
        </Alert>
      ) : (
        <Card className="mt-4 max-h-[400px] overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted sticky top-0">
              <tr className="border-b">
                <th className="p-3 w-10">
                  <Checkbox
                    checked={isAllSelected}
                    onCheckedChange={handleSelectAll}
                    aria-label="Select all"
                  />
                </th>
                <th className="text-left p-3 font-medium">工单号</th>
                <th className="text-left p-3 font-medium">工作中心</th>
                <th className="text-left p-3 font-medium">产品</th>
                <th className="text-left p-3 font-medium">计划数量</th>
                <th className="text-left p-3 font-medium">状态</th>
              </tr>
            </thead>
            <tbody>
              {unassignedPRDs.map((prd) => {
                const isSelected = selectedPRDs.some(p => p._id === prd._id);
                const productDisplay = prd.items?.length > 0
                  ? prd.items.map(i => i.finished_product_code).join(', ')
                  : prd.finished_product_code || '-';
                const totalQty = prd.items?.length > 0
                  ? prd.items.reduce((s, i) => s + (i.planned_quantity || 0), 0)
                  : prd.planned_quantity || 0;

                return (
                  <tr
                    key={prd._id}
                    className={`border-b hover:bg-muted cursor-pointer ${
                      isSelected ? 'bg-primary/10' : ''
                    }`}
                    onClick={() => handlePRDSelection(prd, !isSelected)}
                  >
                    <td className="p-3">
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={(checked) => handlePRDSelection(prd, checked)}
                        aria-label={`Select ${prd.production_order_id}`}
                      />
                    </td>
                    <td className="p-3">{prd.production_order_id || prd._id}</td>
                    <td className="p-3">{prd.work_center_name || '-'}</td>
                    <td className="p-3">{productDisplay}</td>
                    <td className="p-3">{totalQty}</td>
                    <td className="p-3">
                      <Badge variant={prd.status === 'released' ? 'default' : 'outline'}>
                        {prd.status || 'draft'}
                      </Badge>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}

      {selectedPRDs.length > 0 && (
        <Alert className="mt-4 border-green-500 bg-success/10">
          <AlertDescription>
            已选择 {selectedPRDs.length} 个工单
          </AlertDescription>
        </Alert>
      )}
    </div>
  );

  // Step 2: Review and confirm
  const renderStep2Confirm = () => (
    <div>
      <p className="text-sm text-muted-foreground mb-4">
        确认以下关联信息后提交（支持修改分配数量）
      </p>

      {prdMappings.length === 0 ? (
        <Alert variant="destructive">
          <AlertDescription>所选工单中没有可匹配的产品</AlertDescription>
        </Alert>
      ) : (
        <div className="mt-4 space-y-4">
          {prdMappings.map((prd, prdIndex) => (
            <Card key={prd.prd_id} className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Factory className="h-4 w-4 text-purple-500" />
                <h4 className="text-sm font-semibold">{prd.prd_number}</h4>
                {prd.work_center_name && (
                  <span className="text-xs text-muted-foreground">({prd.work_center_name})</span>
                )}
              </div>
              <Separator className="my-2" />

              <div className="max-h-[300px] overflow-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted">
                    <tr className="border-b">
                      <th className="text-left p-2 font-medium">产品编号</th>
                      <th className="text-left p-2 font-medium">产品名称</th>
                      <th className="text-right p-2 font-medium">SO需求</th>
                      <th className="text-right p-2 font-medium">工单计划</th>
                      <th className="text-right p-2 font-medium">分配数量</th>
                      <th className="text-center p-2 font-medium">匹配</th>
                    </tr>
                  </thead>
                  <tbody>
                    {prd.item_mappings.map((im, itemIndex) => (
                      <tr key={`${im.prd_item_index}-${itemIndex}`} className="border-b">
                        <td className="p-2">{im.product_code || '-'}</td>
                        <td className="p-2">{im.so_product_name || '-'}</td>
                        <td className="p-2 text-right">{im.so_required_qty || 0}</td>
                        <td className="p-2 text-right">{im.prd_planned_qty}</td>
                        <td className="p-2 text-right">
                          <Input
                            type="number"
                            value={im.allocated_qty}
                            onChange={(e) => handleQuantityChange(prdIndex, itemIndex, e.target.value)}
                            min={0}
                            max={Math.max(im.so_required_qty || 0, im.prd_planned_qty)}
                            step={1}
                            className="w-24 text-right"
                          />
                        </td>
                        <td className="p-2 text-center">
                          {im.match ? (
                            <CheckCircle className="h-4 w-4 text-success mx-auto" />
                          ) : (
                            <AlertTriangle className="h-4 w-4 text-warning mx-auto" title="无匹配产品" />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return renderStep1SelectPRDs();
      case 1:
        return renderStep2Confirm();
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl min-h-[500px] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5" />
            关联现有工单到销售订单
          </DialogTitle>
        </DialogHeader>

        <Separator />

        <div className="flex-1 overflow-auto py-4">
          <Stepper />

          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription className="flex items-center justify-between">
                {error}
                <Button variant="ghost" size="sm" onClick={() => setError('')}>
                  关闭
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {renderStepContent()}
        </div>

        <Separator />

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleClose} disabled={loading || submitting}>
            取消
          </Button>

          {activeStep > 0 && (
            <Button variant="outline" onClick={handleBack} disabled={loading || submitting}>
              上一步
            </Button>
          )}

          {activeStep < steps.length - 1 ? (
            <Button
              onClick={handleNext}
              disabled={loading || (activeStep === 0 && selectedPRDs.length === 0)}
            >
              下一步
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              <Link2 className="h-4 w-4 mr-2" />
              确认关联
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LinkProductionDialog;
